import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const Base64Utility: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState(() => localStorage.getItem('base64-input') || '');
  const [output, setOutput] = useState(() => localStorage.getItem('base64-output') || '');
  const [error, setError] = useState('');

  React.useEffect(() => {
    localStorage.setItem('base64-input', input);
  }, [input]);

  React.useEffect(() => {
    localStorage.setItem('base64-output', output);
  }, [output]);

  const encodeBase64 = () => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      setError('');
    } catch (err) {
      setError('Error encoding to Base64');
    }
  };

  const decodeBase64 = () => {
    try {
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      setError('');
    } catch (err) {
      setError('Invalid Base64 input');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
    setError('');
  };

  return (
    <Box>
      <UtilityHeader 
        title="Base64 Tool" 
        description="Transform text to Base64 and back with support for UTF-8 characters." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.3) }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Input"
            rows={12}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste or type text here..."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Output"
            accentColor="secondary"
            rows={12}
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="Result will appear here..."
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 2 }}>
        <GlowButton
          size="large"
          onClick={encodeBase64}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Encode
        </GlowButton>
        <GlowButton
          size="large"
          color="secondary"
          glowColor="secondary"
          onClick={decodeBase64}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Decode
        </GlowButton>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <GlowButton
          variant="outlined"
          disableGlow
          onClick={swapInputOutput}
          disabled={!input.trim() && !output.trim()}
          startIcon={<SwapHoriz />}
          sx={{ color: 'text.primary', borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Swap
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!input.trim() && !output.trim()}
          sx={{ opacity: 0.7, borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Clear
        </GlowButton>
      </Stack>
    </Box>
  );
};

export default Base64Utility;
